import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchCurrentUser, updateCurrentUser } from "../api/client";
import type { UserProfile } from "../api/types";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

export default function ProfilePage() {
  const { t } = useTranslation("pages");

  const FEEDBACK_LANGUAGES = useMemo(
    () => [
      { value: "Turkish", label: t("profile.feedbackLanguages.Turkish") },
      { value: "English", label: t("profile.feedbackLanguages.English") },
      { value: "Spanish", label: t("profile.feedbackLanguages.Spanish") },
      { value: "French", label: t("profile.feedbackLanguages.French") },
      { value: "German", label: t("profile.feedbackLanguages.German") },
      { value: "Chinese", label: t("profile.feedbackLanguages.Chinese") },
      { value: "Arabic", label: t("profile.feedbackLanguages.Arabic") },
    ],
    [t],
  );

  const roleLabelMap: Record<string, string> = useMemo(
    () => ({
      ADMIN: t("common.roles.admin"),
      ORG_MANAGER: t("common.roles.manager"),
      TEACHER: t("common.roles.teacher"),
      STANDARD: t("common.roles.standard"),
    }),
    [t],
  );

  const [user, setUser] = useState<UserProfile | null>(null);
  const [feedbackLanguage, setFeedbackLanguage] = useState("Turkish");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentUser()
      .then((u) => {
        setUser(u);
        setFeedbackLanguage(u.feedback_language || "Turkish");
      })
      .catch(() => {});
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("saving");
    setError(null);
    try {
      const updated = await updateCurrentUser({ feedback_language: feedbackLanguage });
      setUser(updated);
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 2500);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : t("profile.saveError"));
    }
  };

  if (!user) {
    return (
      <div className="animate-fade-in">
        <p className="text-sm text-muted">{t("profile.loading")}</p>
      </div>
    );
  }

  const roleLabel =
    roleLabelMap[user.membership_role || ""] ||
    roleLabelMap[user.user_role] ||
    user.user_role;

  return (
    <div className="animate-fade-in" style={{ maxWidth: 560 }}>
      <h2 className="section-title mb-24">{t("profile.title")}</h2>

      {/* User info card */}
      <div className="card card-padding mb-24">
        <h3 className="card-title mb-16">{t("profile.accountCard")}</h3>
        <div className="flex flex-col gap-12">
          <div className="flex items-center gap-12">
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--color-primary)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "var(--text-lg)",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {(user.full_name
                ? user.full_name.split(" ").map((w) => w[0]).join("").slice(0, 2)
                : user.email[0]
              ).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: "var(--text-base)" }}>
                {user.full_name || "—"}
              </div>
              <div className="text-sm text-muted">{user.email}</div>
            </div>
          </div>
          <div className="flex gap-8">
            <span
              className="chip"
              style={{
                background: "var(--color-bg-subtle)",
                color: "var(--text-secondary)",
                fontSize: "var(--text-xs)",
              }}
            >
              {roleLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Settings card */}
      <form onSubmit={onSubmit}>
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-16">{t("profile.preferencesCard")}</h3>

          <div className="form-group">
            <label className="form-label">{t("profile.feedbackLangLabel")}</label>
            <p className="text-xs text-muted mb-8">
              {t("profile.feedbackLangHint")}
            </p>
            <select
              className="form-select"
              value={feedbackLanguage}
              onChange={(e) => setFeedbackLanguage(e.target.value)}
            >
              {FEEDBACK_LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">{t("profile.uiLangLabel")}</label>
            <div style={{ marginTop: 4 }}>
              <LanguageSwitcher variant="header" />
            </div>
          </div>
        </div>

        {error && (
          <div
            className="mb-16"
            style={{
              padding: "10px 14px",
              borderRadius: "var(--radius-md)",
              background: "var(--color-danger-bg)",
              color: "var(--color-danger)",
              fontSize: "var(--text-sm)",
            }}
          >
            {error}
          </div>
        )}

        {status === "saved" && (
          <div
            className="mb-16"
            style={{
              padding: "10px 14px",
              borderRadius: "var(--radius-md)",
              background: "var(--color-success-bg, #ecfdf5)",
              color: "var(--color-success, #16a34a)",
              fontSize: "var(--text-sm)",
            }}
          >
            {t("profile.saved")}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={status === "saving"}
        >
          {status === "saving" ? t("common.saving") : t("common.save")}
        </button>
      </form>
    </div>
  );
}
